import React from 'react';
import { DocsLayout } from '../../components/docs/DocsLayout';
import { CodeBlock } from '../../components/docs/CodeBlock';
import { Callout } from '../../components/docs/Callout';
import { ArrowRight } from 'lucide-react';
export function APIConventionsPage() {
  const authHeadersCode = {
    curl: `curl https://api.obseq.com/v1/payments/payouts \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Obseq-Account: acct_sub_123"`,
    node: `const headers = {
  "Authorization": \`Bearer \${process.env.OBSEQ_API_KEY}\`,
  "Obseq-Account": "acct_sub_123"
};`,
    python: `headers = {
    "Authorization": f"Bearer {os.environ['OBSEQ_API_KEY']}",
    "Obseq-Account": "acct_sub_123"
}`,
    go: `req.Header.Set("Authorization", "Bearer "+apiKey)
req.Header.Set("Obseq-Account", "acct_sub_123")`,
    java: `request.addHeader("Authorization", "Bearer " + apiKey);
request.addHeader("Obseq-Account", "acct_sub_123");`,
    php: `$headers = [
    'Authorization' => 'Bearer ' . $apiKey,
    'Obseq-Account' => 'acct_sub_123'
];`,
    ruby: `headers = {
  'Authorization' => "Bearer #{api_key}",
  'Obseq-Account' => 'acct_sub_123'
}`,
    dotnet: `request.Headers.Add("Authorization", $"Bearer {apiKey}");
request.Headers.Add("Obseq-Account", "acct_sub_123");`
  };
  const idempotencyCode = {
    curl: `curl -X POST https://api.obseq.com/v1/payments/payouts \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Idempotency-Key: 550e8400-e29b-41d4-a716-446655440000"`,
    node: `const headers = {
  "Authorization": \`Bearer \${apiKey}\`,
  "Idempotency-Key": crypto.randomUUID()
};`,
    python: `import uuid

headers = {
    "Authorization": f"Bearer {api_key}",
    "Idempotency-Key": str(uuid.uuid4())
}`,
    go: `import "github.com/google/uuid"

req.Header.Set("Idempotency-Key", uuid.New().String())`
  };
  return <DocsLayout breadcrumbs={[{
    label: 'Docs',
    href: '/docs'
  }, {
    label: 'API Reference',
    href: '/docs/api-reference'
  }, {
    label: 'API Conventions',
    href: '/docs/api-conventions'
  }]} onThisPage={[{
    title: 'Base URL & Versioning',
    id: 'base-url'
  }, {
    title: 'Authentication',
    id: 'authentication'
  }, {
    title: 'Idempotency',
    id: 'idempotency'
  }, {
    title: 'Pagination',
    id: 'pagination'
  }, {
    title: 'Resource Envelope',
    id: 'resource-envelope'
  }, {
    title: 'Status Lifecycle',
    id: 'status-lifecycle'
  }, {
    title: 'Money Fields',
    id: 'money-fields'
  }, {
    title: 'Error Schema',
    id: 'error-schema'
  }, {
    title: 'Webhook Schema',
    id: 'webhook-schema'
  }, {
    title: 'Naming Conventions',
    id: 'naming-conventions'
  }]} data-id="element-771">
      <h1 data-id="element-772">API Conventions (v1)</h1>
      <p className="lead" data-id="element-773">
        Unified consistency specification for the Obseq API. All endpoints
        follow these patterns.
      </p>

      <h2 id="base-url" data-id="element-774">Base URL & Versioning</h2>
      <p data-id="element-775">All API requests use the same base URL with version prefix:</p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg" data-id="element-776">
        <code data-id="element-777">https://api.obseq.com/v1</code>
      </pre>
      <p data-id="element-778">
        Sandbox and production environments share the same interface. Use
        separate API keys per environment.
      </p>

      <h2 id="authentication" data-id="element-779">Authentication</h2>
      <p data-id="element-780">All requests require a Bearer token in the Authorization header:</p>
      <CodeBlock code={authHeadersCode} data-id="element-781" />
      <p data-id="element-782">
        Optional: When acting on behalf of a platform's sub-account, include the{' '}
        <code data-id="element-783">Obseq-Account</code> header.
      </p>

      <h2 id="idempotency" data-id="element-784">Idempotency</h2>
      <p data-id="element-785">
        For unsafe operations (POST/PUT), include an{' '}
        <code data-id="element-786">Idempotency-Key</code> header to ensure the same request isn't
        processed twice:
      </p>
      <CodeBlock code={idempotencyCode} data-id="element-787" />
      <p data-id="element-788">
        Same key + same route + same body → same result. Use UUIDs for
        idempotency keys.
      </p>
      <Callout type="tip" data-id="element-789">
        Store idempotency keys with your request metadata to safely retry failed
        requests.
      </Callout>

      <h2 id="pagination" data-id="element-790">Pagination</h2>
      <p data-id="element-791">List endpoints use cursor-based pagination. Responses include:</p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto" data-id="element-792">
        <code data-id="element-793">{`{
  "data": [...],
  "has_more": true,
  "next_cursor": "cur_abc123"
}`}</code>
      </pre>

      <h3 data-id="element-794">Parameters</h3>
      <table className="w-full text-sm my-4" data-id="element-795">
        <thead data-id="element-796">
          <tr className="border-b border-slate-200" data-id="element-797">
            <th className="text-left py-2 pr-4" data-id="element-798">Parameter</th>
            <th className="text-left py-2 pr-4" data-id="element-799">Type</th>
            <th className="text-left py-2" data-id="element-800">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-801">
          <tr className="border-b border-slate-100" data-id="element-802">
            <td className="py-2 pr-4" data-id="element-803">
              <code data-id="element-804">limit</code>
            </td>
            <td className="py-2 pr-4" data-id="element-805">integer</td>
            <td className="py-2" data-id="element-806">Number of results (default: 25, max: 100)</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-807">
            <td className="py-2 pr-4" data-id="element-808">
              <code data-id="element-809">starting_after</code>
            </td>
            <td className="py-2 pr-4" data-id="element-810">string</td>
            <td className="py-2" data-id="element-811">Cursor for next page</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-812">
            <td className="py-2 pr-4" data-id="element-813">
              <code data-id="element-814">ending_before</code>
            </td>
            <td className="py-2 pr-4" data-id="element-815">string</td>
            <td className="py-2" data-id="element-816">Cursor for previous page</td>
          </tr>
        </tbody>
      </table>

      <h2 id="resource-envelope" data-id="element-817">Common Resource Envelope</h2>
      <p data-id="element-818">Most resources share these standard fields:</p>
      <table className="w-full text-sm my-4" data-id="element-819">
        <thead data-id="element-820">
          <tr className="border-b border-slate-200" data-id="element-821">
            <th className="text-left py-2 pr-4" data-id="element-822">Field</th>
            <th className="text-left py-2 pr-4" data-id="element-823">Type</th>
            <th className="text-left py-2" data-id="element-824">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-825">
          <tr className="border-b border-slate-100" data-id="element-826">
            <td className="py-2 pr-4" data-id="element-827">
              <code data-id="element-828">id</code>
            </td>
            <td className="py-2 pr-4" data-id="element-829">string</td>
            <td className="py-2" data-id="element-830">
              Unique identifier with prefix (e.g., po_...)
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-831">
            <td className="py-2 pr-4" data-id="element-832">
              <code data-id="element-833">object</code>
            </td>
            <td className="py-2 pr-4" data-id="element-834">string</td>
            <td className="py-2" data-id="element-835">Resource type (e.g., "payments.payout")</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-836">
            <td className="py-2 pr-4" data-id="element-837">
              <code data-id="element-838">created_at</code>
            </td>
            <td className="py-2 pr-4" data-id="element-839">string</td>
            <td className="py-2" data-id="element-840">ISO 8601 UTC timestamp</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-841">
            <td className="py-2 pr-4" data-id="element-842">
              <code data-id="element-843">updated_at</code>
            </td>
            <td className="py-2 pr-4" data-id="element-844">string</td>
            <td className="py-2" data-id="element-845">ISO 8601 UTC timestamp</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-846">
            <td className="py-2 pr-4" data-id="element-847">
              <code data-id="element-848">status</code>
            </td>
            <td className="py-2 pr-4" data-id="element-849">string</td>
            <td className="py-2" data-id="element-850">Current state (see lifecycle below)</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-851">
            <td className="py-2 pr-4" data-id="element-852">
              <code data-id="element-853">metadata</code>
            </td>
            <td className="py-2 pr-4" data-id="element-854">object</td>
            <td className="py-2" data-id="element-855">Key-value pairs for custom data</td>
          </tr>
        </tbody>
      </table>

      <h2 id="status-lifecycle" data-id="element-856">Status Lifecycle Pattern</h2>
      <p data-id="element-857">Most resources follow this standard lifecycle:</p>

      {/* Status Flow Diagram */}
      <div className="my-8 bg-slate-50 border border-slate-200 rounded-lg p-8" data-id="element-858">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-859">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg px-4 py-3 text-center min-w-[120px]" data-id="element-860">
            <span className="text-sm font-semibold text-blue-900" data-id="element-861">created</span>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 rotate-90 md:rotate-0" data-id="element-862" />
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg px-4 py-3 text-center min-w-[120px]" data-id="element-863">
            <span className="text-sm font-semibold text-yellow-900" data-id="element-864">
              pending
            </span>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 rotate-90 md:rotate-0" data-id="element-865" />
          <div className="flex flex-col gap-2" data-id="element-866">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg px-4 py-3 text-center min-w-[120px]" data-id="element-867">
              <span className="text-sm font-semibold text-green-900" data-id="element-868">
                succeeded
              </span>
            </div>
            <div className="bg-red-50 border-2 border-red-200 rounded-lg px-4 py-3 text-center min-w-[120px]" data-id="element-869">
              <span className="text-sm font-semibold text-red-900" data-id="element-870">failed</span>
            </div>
            <div className="bg-slate-100 border-2 border-slate-300 rounded-lg px-4 py-3 text-center min-w-[120px]" data-id="element-871">
              <span className="text-sm font-semibold text-slate-700" data-id="element-872">
                canceled
              </span>
            </div>
          </div>
        </div>
      </div>

      <p data-id="element-873">
        Terminal states: <code data-id="element-874">succeeded</code>, <code data-id="element-875">failed</code>,{' '}
        <code data-id="element-876">canceled</code>
      </p>

      <h2 id="money-fields" data-id="element-877">Money Fields</h2>
      <p data-id="element-878">All monetary amounts follow these conventions:</p>
      <table className="w-full text-sm my-4" data-id="element-879">
        <thead data-id="element-880">
          <tr className="border-b border-slate-200" data-id="element-881">
            <th className="text-left py-2 pr-4" data-id="element-882">Field</th>
            <th className="text-left py-2 pr-4" data-id="element-883">Type</th>
            <th className="text-left py-2" data-id="element-884">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-885">
          <tr className="border-b border-slate-100" data-id="element-886">
            <td className="py-2 pr-4" data-id="element-887">
              <code data-id="element-888">amount</code>
            </td>
            <td className="py-2 pr-4" data-id="element-889">integer</td>
            <td className="py-2" data-id="element-890">
              Amount in minor units (e.g., cents for USD)
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-891">
            <td className="py-2 pr-4" data-id="element-892">
              <code data-id="element-893">currency</code>
            </td>
            <td className="py-2 pr-4" data-id="element-894">string</td>
            <td className="py-2" data-id="element-895">Three-letter ISO 4217 currency code</td>
          </tr>
        </tbody>
      </table>

      <Callout type="note" data-id="element-896">
        Example: $250.00 USD ={' '}
        <code data-id="element-897">{`{"amount": 25000, "currency": "USD"}`}</code>
      </Callout>

      <h2 id="error-schema" data-id="element-898">Error Schema</h2>
      <p data-id="element-899">All errors return a consistent structure:</p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4" data-id="element-900">
        <code data-id="element-901">{`{
  "error": {
    "type": "invalid_request_error",
    "code": "parameter_missing",
    "message": "The parameter 'amount' is required.",
    "param": "amount",
    "doc_url": "https://docs.obseq.com/docs/guides/errors#parameter_missing",
    "request_id": "req_01J9Z8V6X0C2H9J3Y7JQ3D2N5P",
    "details": [
      {
        "field": "amount",
        "reason": "required",
        "value": null
      }
    ]
  }
}`}</code>
      </pre>

      <h3 data-id="element-902">Error Types & HTTP Codes</h3>
      <table className="w-full text-sm my-4" data-id="element-903">
        <thead data-id="element-904">
          <tr className="border-b border-slate-200" data-id="element-905">
            <th className="text-left py-2 pr-4" data-id="element-906">Error Type</th>
            <th className="text-left py-2 pr-4" data-id="element-907">HTTP Code</th>
            <th className="text-left py-2" data-id="element-908">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-909">
          <tr className="border-b border-slate-100" data-id="element-910">
            <td className="py-2 pr-4" data-id="element-911">
              <code data-id="element-912">invalid_request_error</code>
            </td>
            <td className="py-2 pr-4" data-id="element-913">400</td>
            <td className="py-2" data-id="element-914">Bad parameters or invalid state</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-915">
            <td className="py-2 pr-4" data-id="element-916">
              <code data-id="element-917">authentication_error</code>
            </td>
            <td className="py-2 pr-4" data-id="element-918">401</td>
            <td className="py-2" data-id="element-919">Missing or invalid API key</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-920">
            <td className="py-2 pr-4" data-id="element-921">
              <code data-id="element-922">authorization_error</code>
            </td>
            <td className="py-2 pr-4" data-id="element-923">403</td>
            <td className="py-2" data-id="element-924">Insufficient permissions</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-925">
            <td className="py-2 pr-4" data-id="element-926">
              <code data-id="element-927">not_found_error</code>
            </td>
            <td className="py-2 pr-4" data-id="element-928">404</td>
            <td className="py-2" data-id="element-929">Unknown resource</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-930">
            <td className="py-2 pr-4" data-id="element-931">
              <code data-id="element-932">conflict_error</code>
            </td>
            <td className="py-2 pr-4" data-id="element-933">409</td>
            <td className="py-2" data-id="element-934">Idempotency conflict or duplicate</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-935">
            <td className="py-2 pr-4" data-id="element-936">
              <code data-id="element-937">rate_limit_error</code>
            </td>
            <td className="py-2 pr-4" data-id="element-938">429</td>
            <td className="py-2" data-id="element-939">Too many requests</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-940">
            <td className="py-2 pr-4" data-id="element-941">
              <code data-id="element-942">api_error</code>
            </td>
            <td className="py-2 pr-4" data-id="element-943">500-503</td>
            <td className="py-2" data-id="element-944">Server error</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-945">
            <td className="py-2 pr-4" data-id="element-946">
              <code data-id="element-947">compliance_error</code>
            </td>
            <td className="py-2 pr-4" data-id="element-948">403</td>
            <td className="py-2" data-id="element-949">Blocked by compliance rules</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-950">
            <td className="py-2 pr-4" data-id="element-951">
              <code data-id="element-952">liquidity_error</code>
            </td>
            <td className="py-2 pr-4" data-id="element-953">400</td>
            <td className="py-2" data-id="element-954">Insufficient liquidity</td>
          </tr>
        </tbody>
      </table>

      <h2 id="webhook-schema" data-id="element-955">Webhook Schema</h2>
      <p data-id="element-956">All webhook events follow this structure:</p>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4" data-id="element-957">
        <code data-id="element-958">{`{
  "id": "evt_01J9Z9A9Y8Y7R7F8N4W9N6W3G2",
  "type": "payments.payout.succeeded",
  "created_at": "2026-01-03T18:41:22Z",
  "api_version": "v1",
  "request_id": "req_01J9Z9A9Y8ZK2K2FQK9Z0X8C1D",
  "data": {
    "object": {
      "id": "po_01J9Z98K5K8C0MZ9QK3G4B1E2P",
      "object": "payments.payout",
      "status": "succeeded",
      ...
    }
  }
}`}</code>
      </pre>

      {/* Webhook Flow Diagram */}
      <div className="my-8 bg-slate-50 border border-slate-200 rounded-lg p-8" data-id="element-959">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-960">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg px-4 py-3 text-center min-w-[140px]" data-id="element-961">
            <span className="text-sm font-semibold text-blue-900" data-id="element-962">
              Obseq Event
            </span>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 rotate-90 md:rotate-0" data-id="element-963" />
          <div className="bg-slate-100 border-2 border-slate-300 rounded-lg px-4 py-3 text-center min-w-[140px]" data-id="element-964">
            <span className="text-sm font-semibold text-slate-700" data-id="element-965">
              Your Endpoint
            </span>
          </div>
          <ArrowRight className="w-5 h-5 text-slate-400 rotate-90 md:rotate-0" data-id="element-966" />
          <div className="bg-green-50 border-2 border-green-200 rounded-lg px-4 py-3 text-center min-w-[140px]" data-id="element-967">
            <span className="text-sm font-semibold text-green-900" data-id="element-968">200 OK</span>
          </div>
        </div>
      </div>

      <Callout type="warning" data-id="element-969">
        Verify webhook signatures to ensure events are from Obseq. See the{' '}
        <a href="#/docs/guides/webhooks" className="text-blue-600 hover:text-blue-700" data-id="element-970">
          Webhooks Guide
        </a>{' '}
        for implementation details.
      </Callout>

      <h2 id="naming-conventions" data-id="element-971">Naming Conventions</h2>

      <h3 data-id="element-972">Resource ID Prefixes</h3>
      <p data-id="element-973">All resource IDs use consistent prefixes:</p>
      <table className="w-full text-sm my-4" data-id="element-974">
        <thead data-id="element-975">
          <tr className="border-b border-slate-200" data-id="element-976">
            <th className="text-left py-2 pr-4" data-id="element-977">Prefix</th>
            <th className="text-left py-2 pr-4" data-id="element-978">Resource</th>
            <th className="text-left py-2" data-id="element-979">Example</th>
          </tr>
        </thead>
        <tbody data-id="element-980">
          <tr className="border-b border-slate-100" data-id="element-981">
            <td className="py-2 pr-4" data-id="element-982">
              <code data-id="element-983">po_</code>
            </td>
            <td className="py-2 pr-4" data-id="element-984">Payout</td>
            <td className="py-2" data-id="element-985">
              <code data-id="element-986">po_01J9Z98K5K8C0MZ9QK3G4B1E2P</code>
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-987">
            <td className="py-2 pr-4" data-id="element-988">
              <code data-id="element-989">co_</code>
            </td>
            <td className="py-2 pr-4" data-id="element-990">Collection</td>
            <td className="py-2" data-id="element-991">
              <code data-id="element-992">co_01J9Z98K5K8C0MZ9QK3G4B1E2P</code>
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-993">
            <td className="py-2 pr-4" data-id="element-994">
              <code data-id="element-995">wa_</code>
            </td>
            <td className="py-2 pr-4" data-id="element-996">Wallet</td>
            <td className="py-2" data-id="element-997">
              <code data-id="element-998">wa_01J9Z98K5K8C0MZ9QK3G4B1E2P</code>
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-999">
            <td className="py-2 pr-4" data-id="element-1000">
              <code data-id="element-1001">tr_</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1002">Transfer</td>
            <td className="py-2" data-id="element-1003">
              <code data-id="element-1004">tr_01J9Z98K5K8C0MZ9QK3G4B1E2P</code>
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1005">
            <td className="py-2 pr-4" data-id="element-1006">
              <code data-id="element-1007">ex_</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1008">Exchange Trade</td>
            <td className="py-2" data-id="element-1009">
              <code data-id="element-1010">ex_01J9Z98K5K8C0MZ9QK3G4B1E2P</code>
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1011">
            <td className="py-2 pr-4" data-id="element-1012">
              <code data-id="element-1013">cp_</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1014">Compliance Profile</td>
            <td className="py-2" data-id="element-1015">
              <code data-id="element-1016">cp_01J9Z98K5K8C0MZ9QK3G4B1E2P</code>
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1017">
            <td className="py-2 pr-4" data-id="element-1018">
              <code data-id="element-1019">lq_</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1020">Liquidity Execution</td>
            <td className="py-2" data-id="element-1021">
              <code data-id="element-1022">lq_01J9Z98K5K8C0MZ9QK3G4B1E2P</code>
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1023">
            <td className="py-2 pr-4" data-id="element-1024">
              <code data-id="element-1025">tk_</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1026">Tokenize Asset</td>
            <td className="py-2" data-id="element-1027">
              <code data-id="element-1028">tk_01J9Z98K5K8C0MZ9QK3G4B1E2P</code>
            </td>
          </tr>
        </tbody>
      </table>

      <h3 data-id="element-1029">Event Naming</h3>
      <p data-id="element-1030">
        Webhook events follow the pattern: <code data-id="element-1031">product.resource.action</code>
      </p>
      <ul data-id="element-1032">
        <li data-id="element-1033">
          <code data-id="element-1034">payments.payout.succeeded</code>
        </li>
        <li data-id="element-1035">
          <code data-id="element-1036">wallets.transfer.created</code>
        </li>
        <li data-id="element-1037">
          <code data-id="element-1038">exchange.trade.executed</code>
        </li>
        <li data-id="element-1039">
          <code data-id="element-1040">compliance.verification.completed</code>
        </li>
      </ul>

      <div className="mt-12 pt-8 border-t border-slate-200" data-id="element-1041">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-1042">
          Related Pages
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1043">
          <a href="#/docs/api-reference" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1044">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1045">API Reference</h4>
            <p className="text-sm text-slate-600" data-id="element-1046">
              Explore all product endpoints
            </p>
          </a>
          <a href="#/docs/guides/errors" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1047">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1048">
              Error Handling
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1049">Handle errors gracefully</p>
          </a>
        </div>
      </div>
    </DocsLayout>;
}